import {
  Component,
  inject,
  OnInit,
  Output,
  EventEmitter,
  signal,
  computed,
  ChangeDetectionStrategy,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CurrencyAmountDisplay } from 'src/app/core/services/currency/currency-format';
import { PAGE } from 'src/app/routes';
import { GameScoreService } from 'src/app/core/services/game-score/game-score.service';
import { NavTab } from 'src/app/core/services/theme/theme-config';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { TranslocoService } from '@jsverse/transloco';
import { GameId } from 'src/app/model/game/game-id';
import { gameIdToName } from 'src/app/model/game/game-id.utils';
import { GameInterfaceState } from '../game/controllers/listeners-legacy/ux-game-listener';
import { TabOption, NavbarComponent } from './navbar/navbar.component';
import { CheatSheetComponent } from '../cheat-sheet/cheat-sheet.component';
import { ModalController, Platform } from '@ionic/angular/standalone';
import {
  getModalConfig,
  safeModalDismiss,
  shouldShowAsSheetModal,
} from 'src/app/shared/utils/modal.utils';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { UxGameService } from 'src/app/core/services/ux-game/ux-game.service';
import { GameReplayComponent } from './game-replay/game-replay.component';
import { GameScoreComponent } from './game-score/game-score.component';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NavbarComponent, GameScoreComponent, GameReplayComponent],
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  @Output() closeClicked = new EventEmitter<void>();
  @Output() tabSelected = new EventEmitter<string>();

  private router = inject(Router);
  private analyticsService = inject(AnalyticsService);
  private uxGameService = inject(UxGameService);
  private cdr = inject(ChangeDetectorRef);

  operatorService = inject(OperatorService);
  translocoService = inject(TranslocoService);
  themeService = inject(ThemeService);
  gameScoreService = inject(GameScoreService);
  modalController = inject(ModalController);
  platform = inject(Platform);

  private readonly currentUrl = signal<string>(this.router.url);
  private cheatSheetModal?: HTMLIonModalElement;
  private readonly destroy$ = new Subject<void>();

  displayGameInterfaceState = computed(() => {
    if (this.gameScoreService.isPlayingAgain()) {
      return GameInterfaceState.GameStarted;
    }
    return this.gameScoreService.gameInterfaceState();
  });

  showGameScore = computed(() => {
    const url = this.currentUrl();
    const gameState = this.gameScoreService.gameInterfaceState();
    const isPlayingAgain = this.gameScoreService.isPlayingAgain();

    const onGameRoute = url.startsWith(`/${PAGE.GAME}`);

    const isGameActive = gameState === GameInterfaceState.GameStarted;

    return onGameRoute && (isGameActive || isPlayingAgain);
  });

  showGameReplay = computed(() => {
    const url = this.currentUrl();
    const gameState = this.gameScoreService.gameInterfaceState();

    const onGameRoute = url.startsWith(`/${PAGE.GAME}`);

    const isReplayActive =
      gameState === GameInterfaceState.ReplayGameInit ||
      gameState === GameInterfaceState.ReplayReady;

    return onGameRoute && isReplayActive;
  });

  showNavbar = computed(() => {
    if (this.operatorService.isGameOnlyOperator()) {
      return false;
    }

    const url = this.currentUrl();
    const gameState = this.gameScoreService.gameInterfaceState();
    const isPlayingAgain = this.gameScoreService.isPlayingAgain();

    const onGameRoute = url.startsWith(`/${PAGE.GAME}`);
    if (onGameRoute) {
      return !isPlayingAgain && gameState !== GameInterfaceState.GameStarted;
    }

    const hideNavbarRoutes = ['/'];

    const hide =
      hideNavbarRoutes.includes(url) || url.startsWith('/?') || url.startsWith('/?redirect=true');

    return !hide;
  });

  GameId = GameId;
  OperatorService = OperatorService;

  constructor() {
    this.translocoService.langChanges$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  ngOnInit() {
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.urlAfterRedirects); // Use `urlAfterRedirects` for more stability
      }
    });

    this.gameScoreService.autoCloseCheatSheet$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        if (this.cheatSheetModal) {
          await safeModalDismiss(this.modalController);
          this.cheatSheetModal = undefined;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get balance(): CurrencyAmountDisplay | undefined {
    return this.operatorService.formattedBalance() ?? undefined;
  }

  get isDemo(): boolean {
    return OperatorService.IsDemo;
  }

  get tabs(): TabOption[] {
    const allTabs = [
      {
        key: NavTab.play,
        label: this.translocoService.translate('tabs.play'),
        id: `${PAGE.GAME}/${this.operatorService.gameId}`,
      },
      {
        key: NavTab.results,
        label: this.translocoService.translate('tabs.results'),
        id: PAGE.RESULTS,
      },
      {
        key: NavTab.leaderboard,
        label: this.translocoService.translate('tabs.leaderboard'),
        id: PAGE.CHOOSE_LEADERBOARDS,
      },
      {
        key: NavTab.rules,
        label: this.translocoService.translate('tabs.rules'),
        id: `${PAGE.SELECT_RULES}/${this.operatorService.gameId}`,
      },
    ];

    const order = this.themeService.tabConfig().order;
    if (!order?.length) {
      return allTabs;
    }
    return order.flatMap((key) => allTabs.filter((t) => t.key === key));
  }

  get gameId(): GameId {
    return this.operatorService.gameId as GameId;
  }

  get gameName(): string {
    return gameIdToName(this.operatorService.gameId as GameId, this.translocoService);
  }

  onCloseClicked() {
    this.closeClicked.emit();
  }

  onCloseReplayClicked() {
    this.uxGameService.resetToLoading();
  }

  onTabSelected(tabId: string) {
    if (tabId === `${PAGE.GAME}/${this.operatorService.gameId}` || tabId === PAGE.RESULTS) {
      this.operatorService.updateBalance(OperatorService.IsDemo);
    }

    this.tabSelected.emit(tabId);
  }

  onBackClicked() {
    window.location.href = '?redirect=true';
  }

  async onCheatSheetClicked() {
    const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);
    this.cheatSheetModal = await this.modalController.create({
      component: CheatSheetComponent,
      componentProps: { gameId: this.gameId },
      ...getModalConfig(asSheetModal, undefined, { backdropDismiss: true }),
    });

    await this.cheatSheetModal.present();

    this.analyticsService.log(EventName.ClinchGameCheatSheetClick, { gameId: this.gameId });
  }
}
